import {pointerUtils, type CreateExtArgs} from '@wix/document-manager-core'
import type {CompRef, Pointer} from '@wix/document-services-types'
import {displayedOnlyStructureUtil} from '@wix/santa-core-utils'
import _ from 'lodash'
import {getComponentType} from '../../../utils/dalUtils'
import type {ComponentsMetadataAPI} from '../../componentsMetadata/componentsMetadata'
import type {DataModelExtensionAPI} from '../../dataModel/dataModel'
import {VIEW_MODES} from '../../../constants/constants'
import {validateComponentToDelete} from './validation'
import {getComponentConnections, getComponentData} from './dataQuerying'
import {
    deleteForkedMobileData,
    getComponentVariantPointers,
    removeComponentVariants,
    removeFromParent,
    removeScopedValuesReferencingVariants,
    removeTranslationsForComponent
} from './dataCleanup'
import {ghostifyComponent, shouldRemoveAsGhost} from './ghostification'
import {isValidationError, type ComponentRemovalEvents} from './types'

interface RemoveArgs {
    isReplacingComp?: boolean
    [key: string]: unknown
}

type DeleteChildFn = (
    childPointer: CompRef,
    isParentDeleting?: boolean,
    childRemoveArgs?: RemoveArgs,
    isDeletedParentFromFull?: boolean,
    childCallback?: VoidFunction
) => void

const deleteComponentTree = (
    createExtArgs: CreateExtArgs,
    events: ComponentRemovalEvents,
    compPtr: CompRef,
    delParent: boolean | undefined,
    rmArgs: RemoveArgs | undefined,
    delParentFromFull: boolean | undefined,
    callback: VoidFunction,
    deleteChild: DeleteChildFn
): void => {
    const {dal, pointers, extensionAPI, eventEmitter} = createExtArgs
    const {componentsMetadata} = extensionAPI as ComponentsMetadataAPI
    const {dataModel} = extensionAPI as DataModelExtensionAPI

    const parentPointer = pointers.structure.getParent(compPtr)
    const compType = getComponentType(dal, compPtr)

    eventEmitter.emit(
        events.BEFORE_REMOVE,
        compPtr,
        compType,
        delParent,
        rmArgs,
        delParentFromFull,
        callback,
        deleteChild
    )

    const childrenRefs = pointers.structure.getChildren(compPtr)
    _.forEachRight(childrenRefs, (child: Pointer) => {
        const parentOfChild = pointers.structure.getParent(child)
        if (!parentOfChild || parentOfChild.id === compPtr.id) {
            deleteChild(child as CompRef, true, rmArgs, true)
        }
    })

    const copyDataItem = getComponentData(createExtArgs, compPtr)
    const componentConnections = getComponentConnections(createExtArgs, compPtr)

    const isDesktop = compPtr.type !== VIEW_MODES.MOBILE
    const desktopPointer = isDesktop ? compPtr : pointers.structure.getDesktopPointer(compPtr)
    const isMobileOnly = componentsMetadata.isMobileOnly(compPtr)
    const component = dal.get(compPtr)

    let shouldDeleteData: boolean
    if (component && component.type !== 'Page') {
        shouldDeleteData = isDesktop || isMobileOnly
    } else {
        shouldDeleteData = isDesktop
    }
    const dataComponentPointer = component && component.type !== 'Page' && isMobileOnly ? compPtr : desktopPointer

    if (shouldDeleteData) {
        removeTranslationsForComponent(createExtArgs, dataComponentPointer)

        const pageId = pointers.structure.getPageOfComponent(dataComponentPointer)?.id
        const variantPointers = getComponentVariantPointers(createExtArgs, dataComponentPointer, pageId)
        removeScopedValuesReferencingVariants(createExtArgs, variantPointers)

        if (displayedOnlyStructureUtil.isRepeatedComponent(compPtr.id)) {
            const originalId = displayedOnlyStructureUtil.getRepeaterTemplateId(compPtr.id)
            const originalCompPointer = pointerUtils.getPointer(originalId, compPtr.type)
            if (dal.has(originalCompPointer)) {
                dataModel.removeItemRecursively(originalCompPointer)
            }
        }

        dataModel.removeItemRecursively(dataComponentPointer)
        removeComponentVariants(createExtArgs, variantPointers)
    }

    deleteForkedMobileData(createExtArgs, desktopPointer)

    if (!shouldDeleteData || dataComponentPointer !== compPtr) {
        dal.remove(compPtr)
    }
    removeFromParent(createExtArgs, parentPointer, compPtr.id)

    eventEmitter.emit(
        events.AFTER_REMOVE,
        compPtr,
        compType,
        delParent,
        rmArgs,
        delParentFromFull,
        copyDataItem,
        parentPointer,
        componentConnections,
        compType
    )

    callback()
}

export const deleteComponent = (
    createExtArgs: CreateExtArgs,
    events: ComponentRemovalEvents,
    componentPointer: CompRef,
    deletingParent?: boolean,
    removeArgs?: RemoveArgs,
    deletedParentFromFull?: boolean,
    completeCallback?: VoidFunction
): void => {
    if (shouldRemoveAsGhost(createExtArgs, componentPointer)) {
        return ghostifyComponent(createExtArgs, events, componentPointer, completeCallback)
    }

    const {pointers} = createExtArgs
    const {componentsMetadata} = createExtArgs.extensionAPI as ComponentsMetadataAPI
    const compToRemove = pointerUtils.getRepeatedItemPointerIfNeeded(componentPointer) as CompRef
    const callback = completeCallback || _.noop

    let resolvedPointer = compToRemove
    if (!deletingParent && componentsMetadata.shouldBeRemovedByParent(resolvedPointer)) {
        resolvedPointer = pointers.structure.getParent(resolvedPointer) as CompRef
    }

    const validationResult = validateComponentToDelete(createExtArgs, resolvedPointer, deletedParentFromFull)
    if (isValidationError(validationResult)) {
        throw new Error(validationResult.error)
    }

    const deleteChild: DeleteChildFn = (
        childPointer,
        isParentDeleting,
        childRemoveArgs,
        isDeletedParentFromFull,
        childCallback
    ) =>
        deleteComponent(
            createExtArgs,
            events,
            childPointer,
            isParentDeleting,
            childRemoveArgs,
            isDeletedParentFromFull,
            childCallback
        )

    deleteComponentTree(
        createExtArgs,
        events,
        resolvedPointer,
        deletingParent,
        removeArgs,
        deletedParentFromFull,
        callback,
        deleteChild
    )
}
